import { type ReactNode } from 'react';
import { Button } from './Button';

/* ============================================================================
 * State primitives — empty, loading, error.  Used everywhere a list, table
 * or query may have nothing to show.  Every page should pick one of these
 * three rather than leaving a blank surface.
 * ========================================================================= */

type EmptyStateIllustration =
  | 'default'
  | 'list'
  | 'queue-clear'
  | 'no-results'
  | 'inbox';

interface EmptyStateProps {
  title: string;
  description?: string;
  action?: ReactNode;
  /** Eyebrow (small caps label) shown above the title. */
  eyebrow?: string;
  /** Pick the illustration that fits the context. */
  illustration?: EmptyStateIllustration;
}

export function EmptyState({
  title,
  description,
  action,
  eyebrow,
  illustration = 'default',
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <EmptyIllustration variant={illustration} />
      {eyebrow && <p className="eyebrow mb-2 mt-6">{eyebrow}</p>}
      <h3
        className={[
          'text-[18px] font-semibold leading-tight text-[var(--color-ink)]',
          eyebrow ? '' : 'mt-6',
        ].join(' ')}
      >
        {title}
      </h3>
      {description && (
        <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-[var(--color-ink-soft)]">
          {description}
        </p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

/**
 * Hand-drawn-feel empty-state illustrations.  Each is a small line-art
 * SVG anchored on the brand palette.  Stroke-only — no fills — so they
 * render crisply at any size and inherit colour cleanly.
 */
function EmptyIllustration({ variant }: { variant: EmptyStateIllustration }) {
  const stroke = 'var(--color-rule-strong)';
  const accent = 'var(--color-primary)';
  const accentSoft = 'var(--color-primary-soft)';

  if (variant === 'queue-clear') {
    // Empty inbox tray with a checkmark — "nothing to do here"
    return (
      <svg
        aria-hidden="true"
        width="88"
        height="72"
        viewBox="0 0 88 72"
        fill="none"
      >
        <rect x="14" y="20" width="60" height="38" rx="3" stroke={stroke} strokeWidth="1.5" fill={accentSoft} />
        <path d="M14 38 L26 38 L30 44 L58 44 L62 38 L74 38" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="62" cy="22" r="10" fill="white" stroke={accent} strokeWidth="1.8" />
        <path d="M57 22 L61 26 L67 18" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (variant === 'list') {
    // Stack of empty list rows
    return (
      <svg
        aria-hidden="true"
        width="84"
        height="72"
        viewBox="0 0 84 72"
        fill="none"
      >
        <rect x="10" y="14" width="64" height="10" rx="2" stroke={stroke} strokeWidth="1.5" fill="white" />
        <rect x="10" y="30" width="64" height="10" rx="2" stroke={stroke} strokeWidth="1.5" fill="white" />
        <rect x="10" y="46" width="64" height="10" rx="2" stroke={stroke} strokeWidth="1.5" fill="white" />
        <line x1="18" y1="19" x2="46" y2="19" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        <line x1="18" y1="35" x2="40" y2="35" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        <line x1="18" y1="51" x2="50" y2="51" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        <circle cx="68" cy="64" r="6" fill={accent} />
        <path d="M65 64 L67.5 66.5 L72 62" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (variant === 'no-results') {
    // Magnifying glass over an empty page
    return (
      <svg
        aria-hidden="true"
        width="80"
        height="72"
        viewBox="0 0 80 72"
        fill="none"
      >
        <rect x="14" y="12" width="40" height="50" rx="3" stroke={stroke} strokeWidth="1.5" fill="white" />
        <line x1="22" y1="22" x2="44" y2="22" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="22" y1="30" x2="38" y2="30" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        <line x1="22" y1="38" x2="42" y2="38" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3" />
        <circle cx="56" cy="46" r="12" fill={accentSoft} stroke={accent} strokeWidth="2" />
        <line x1="65" y1="55" x2="72" y2="62" stroke={accent} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }

  if (variant === 'inbox') {
    // Generic envelope / inbox
    return (
      <svg
        aria-hidden="true"
        width="80"
        height="64"
        viewBox="0 0 80 64"
        fill="none"
      >
        <rect x="10" y="14" width="60" height="40" rx="3" stroke={stroke} strokeWidth="1.5" fill="white" />
        <path d="M10 17 L40 38 L70 17" stroke={accent} strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }

  // Default — abstract brand mark (used by NotFound's CTA, generic empty)
  return (
    <svg
      aria-hidden="true"
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
    >
      <defs>
        <linearGradient id="prismGrad" x1="0" y1="0" x2="64" y2="64">
          <stop offset="0%" stopColor="#2484c6" />
          <stop offset="100%" stopColor="#008da5" />
        </linearGradient>
      </defs>
      <rect x="12" y="12" width="40" height="40" rx="6" fill="url(#prismGrad)" />
      <rect x="20" y="20" width="24" height="24" rx="2" fill="white" opacity="0.9" />
      <line x1="20" y1="32" x2="44" y2="20" stroke="#1f4d3e" strokeWidth="1.5" />
    </svg>
  );
}

interface LoadingStateProps {
  /** Visible label, also used as the aria-label on the spinner. */
  label?: string;
  /** Compact inline spinner without surrounding padding. */
  inline?: boolean;
}

export function LoadingState({
  label = 'Loading…',
  inline = false,
}: LoadingStateProps) {
  if (inline) {
    return (
      <span
        role="status"
        aria-label={label}
        className="inline-flex items-center gap-2 text-[13px] text-[var(--color-ink-soft)]"
      >
        <Spinner />
        <span>{label}</span>
      </span>
    );
  }
  return (
    <div
      role="status"
      aria-label={label}
      className="flex flex-col items-center justify-center gap-3 px-6 py-12"
    >
      <Spinner size="md" />
      <p className="font-sans text-[13px] text-[var(--color-ink-soft)]">
        {label}
      </p>
    </div>
  );
}

function Spinner({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const sz = size === 'md' ? 'h-6 w-6 border-[2.5px]' : 'h-4 w-4 border-2';
  return (
    <span
      aria-hidden="true"
      className={[
        'inline-block animate-spin rounded-full',
        'border-[var(--color-primary)] border-t-transparent',
        sz,
      ].join(' ')}
    />
  );
}

interface ErrorStateProps {
  title?: string;
  message?: string;
  /** Render a Retry button if provided. */
  onRetry?: () => void;
  retryLabel?: string;
}

export function ErrorState({
  title = 'Could not load this',
  message = 'Something went wrong on our side. The team has been notified — try again in a moment.',
  onRetry,
  retryLabel = 'Try again',
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div
        aria-hidden="true"
        className="mb-5 flex h-12 w-12 items-center justify-center rounded-[6px] bg-[var(--color-danger-soft)] text-[var(--color-danger)]"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <circle cx="12" cy="16" r="0.6" fill="currentColor" />
        </svg>
      </div>
      <h3 className="text-[16px] font-semibold leading-tight text-[var(--color-ink)]">
        {title}
      </h3>
      <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-[var(--color-ink-soft)]">
        {message}
      </p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-5">
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/* ============================================================================
 * Skeleton — pulse-animated placeholders for loading list rows / cards.
 * ========================================================================= */

interface SkeletonProps {
  /** Width as Tailwind arbitrary value (e.g. "200px", "60%", "full"). */
  width?: string;
  /** Height as Tailwind arbitrary value. Defaults to 14px (single line). */
  height?: string;
  /** Use circle shape — for avatars / dot placeholders. */
  circle?: boolean;
  className?: string;
}

export function Skeleton({
  width = '100%',
  height = '14px',
  circle = false,
  className = '',
}: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={[
        'block animate-pulse bg-[var(--color-surface-muted)]',
        circle ? 'rounded-full' : 'rounded-[3px]',
        className,
      ].join(' ')}
      style={{ width, height }}
    />
  );
}

/** Convenience: a small, repeated 3-line block — great for list-row placeholders. */
export function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 border-b border-[var(--color-rule)] px-5 py-4">
      <Skeleton width="40%" height="14px" />
      <Skeleton width="20%" height="14px" />
      <Skeleton width="15%" height="14px" />
      <Skeleton width="15%" height="14px" />
      <Skeleton width="60px" height="22px" />
    </div>
  );
}

/** Inline alert for errors / warnings beside form fields or above a section. */
interface InlineAlertProps {
  variant?: 'danger' | 'warn' | 'info' | 'success';
  children: ReactNode;
}

export function InlineAlert({ variant = 'danger', children }: InlineAlertProps) {
  const tone =
    variant === 'warn'
      ? 'border-[var(--color-warn)] bg-[var(--color-warn-soft)] text-[var(--color-warn-ink)]'
      : variant === 'info'
      ? 'border-[var(--color-info)] bg-[var(--color-info-soft)] text-[var(--color-info-ink)]'
      : variant === 'success'
      ? 'border-[var(--color-success)] bg-[var(--color-success-soft)] text-[var(--color-success-ink)]'
      : 'border-[var(--color-danger)] bg-[var(--color-danger-soft)] text-[var(--color-danger-ink)]';
  return (
    <div
      role="alert"
      className={[
        'rounded-[4px] border-l-[3px] px-4 py-3 text-[13.5px] leading-relaxed',
        tone,
      ].join(' ')}
    >
      {children}
    </div>
  );
}
